/**
 * AiUpscalePipeline — high-level "file in → upscaled file out" orchestrator
 * for Real-ESRGAN ×4 AI video upscale. Wraps the working primitives:
 *   - AIUpscaleCore (model fetch + WebGPU/WASM worker session)
 *   - FFmpegProcessor (frame extract + segment encode + final mux)
 * with one stable public API so multiple tools can share the same flow.
 *
 * Today used by: /video-upscaler (the dedicated landing). enhance-video's
 * existing inline runAiUpscalePipeline() keeps working unchanged; a follow-
 * up cycle can migrate it to this module to delete the duplication.
 *
 * Public surface:
 *   window.AiUpscalePipeline.run({
 *       file, scale, onProgress, signal, slug
 *   }) → Promise<{ blob, file, stats }>
 *   window.AiUpscalePipeline.isSupported() → Promise<boolean>
 *   window.AiUpscalePipeline.computeOutputDims(srcW, srcH, scale) → { w, h }
 *
 * scale: 4 | 'hd720' | 'fhd1080' | 'auto4k' | {w, h}   (×2 удалён 2026-08-19)
 * other opts: probe (skip the file probe), maxFps (60), segBpp (encoder bits/px),
 *   afterCalibrate(async {tileMs, dims, feed}) — awaited before the first frame.
 *   - 4       — run model ×4 native (e.g. 1080p → 4320p; 720p → 2880p)
 *   - 'hd720' / 'fhd1080' / 'auto4k' — the result fits the 1280×720 / 1920×1080 /
 *     3840×2160 box (turned for portrait); ratio clamped to [1, 4]. The model
 *     still sees the full frame — the result is reduced after inference.
 *
 * onProgress({ phase, done, total, etaSec }) phases:
 *   'model_load' (0–18 %)  · 'compile' (18–22 %)
 *   'extract'    (22–25 %) · 'upscale' (25–90 %)  · 'encode' (90–99 %) · 'done'
 *
 * onPartialReady({ blob, secondsCommitted, totalSeconds, segmentIndex, segmentCount })
 *   Optional. Fires after each finished 5-sec chunk gets encoded into its
 *   own MP4 segment — throttled (≥ 3 wall-sec since last emit AND ≥ 5 sec
 *   of new content) so cheap-chunk videos don't spam. blob = cumulative
 *   `-c copy` concat of every completed segment so far (video-only — audio
 *   is muxed into the final result, not the preview, to keep partial
 *   concat cheap). Etalon ported from /ai-music-cleaner streaming preview
 *   (commit 4b0f95ef1): user can verify AI output quality at minute 1 of
 *   a 60-minute job instead of at the end.
 *
 * The chunked extract→upscale→encode loop bounds MEMFS to one 5-second
 * chunk worth of PNGs at a time — proven on 2-minute clips at 60 fps.
 */
(function () {
'use strict';

if (typeof window === 'undefined') return;

// Default chunk size for the streaming pipeline. Adaptive — bigger
// outputs cut to a smaller window so the per-chunk MEMFS staging (input
// PNGs + output PNGs at output resolution) stays below the WASM 4 GB
// heap ceiling. See pickChunkSec().
var CHUNK_SEC = 5;
var DEFAULT_SLUG = 'video-upscaler';

// Bound the per-chunk MEMFS footprint by shrinking the chunk window
// for large outputs. Each frame in MEMFS is roughly width*height*4 bytes
// (RGBA), but PNG compression drops that to ~25-40% on natural content,
// then the OUT PNGs at the same dims are also staged. The 4 GB WASM
// heap is the hard wall — anything close to 1 GB stage starts triggering
// the "out of memory" allocator failures we ship `err_oom` for.
//
// Math: 5 sec × 30 fps = 150 frames. PNG-encoded ≈ 35% of raw RGBA.
//   1080p: 150 × 2.9 MB ≈ 435 MB IN + same OUT = ~870 MB → 5 sec OK
//   1440p: 150 × 5.2 MB ≈ 780 MB IN + same OUT = ~1.5 GB → tight
//   2160p (4K): 150 × 11.6 MB ≈ 1.7 GB IN + same OUT = ~3.5 GB → DROP TO 2 SEC
//   4320p (8K): 150 × 46 MB ≈ 7 GB → impossible at 5 sec → DROP TO 1 SEC
//
// Picks based on output area, not source — we stage AI OUTPUT frames,
// which is what `dims.w × dims.h` represents.
function pickChunkSec(outW, outH) {
    var pixels = (outW || 0) * (outH || 0);
    if (!pixels) return CHUNK_SEC;
    // Boundary at ~8.3 MP (4K = 3840×2160 = 8.3 MP). Above → 2 sec.
    // Boundary at ~33 MP (8K = 7680×4320 = 33 MP). Above → 1 sec.
    if (pixels >= 33000000) return 1;
    if (pixels >= 8000000)  return 2;
    // Wave 24: on lowMem (2-3 GB) devices the tab budget, not the WASM
    // 4 GB wall, is the ceiling — halve the staged window. At the
    // LOWMEM_SRC_PX input cap (640×480 → 4.9 MPx out) a 2 s chunk keeps
    // per-chunk PNG staging ≈ 40-90 MB instead of 100-300+ at 5 s.
    var dm = (typeof navigator !== 'undefined' && navigator.deviceMemory) || 0;
    if (dm && dm < 4) return 2;
    return CHUNK_SEC;
}

function _ae(slug, action, extra) {
    try {
        if (typeof window.__ae !== 'function') return;
        var payload = Object.assign({ slug: slug, action: action }, extra || {});
        window.__ae('feature_use', payload);
    } catch (_) {}
}

/* ── Кодер фрагментов ────────────────────────────────────────────────────────
   До 15.10.2026 каждый готовый кадр шёл в FFmpeg-WASM как PNG (canvas.toBlob,
   главный поток), а фрагмент собирал libx264 внутри WASM. Замер на настоящем
   прогоне (RTX 4060, 640×280 → 2560×1120, «Сбалансированная», 10 с, #1914):
   PNG — 288 мс на кадр, x264 — ~200 мс на кадр (30 с на каждые 5 с видео);
   вместе 36 % всего времени, сама сеть — 55 %. А в СКРЫТОЙ вкладке тот же
   цикл шёл втрое медленнее (896 → 2563 мс на кадр, настоящий Chrome по CDP).
   Это ровно случай общего примитива «кадры рождаются по одному и долго» —
   CanvasVideoExport.openStream (аппаратный H.264, каскад при смерти кодера,
   уступки, живущие в скрытой вкладке); образец — colorize-video-pipeline.js.
   Прежний лейн остаётся там, где кодера нет или он умер. */
/* Bits per pixel — parity with the lane it replaces («PNG → libx264 ultrafast
   crf 18»), MEASURED on real model output (60 frames, «Сбалансированная», 640×280
   → 2560×1120, RTX 4060, SSIM/PSNR against the model's PNGs,
   tests/scratch/vu-encode-parity-bench.mjs):
       x264 crf18 ultrafast   3.62 MB   SSIM 0.9916   PSNR 50.13
       openStream 0.15 bpp    2.70 MB   SSIM 0.9927   PSNR 49.78
       openStream 0.22 bpp    3.16 MB   SSIM 0.9928   PSNR 49.90
   ~50 dB either way, i.e. the encode adds nothing a viewer could see; 0.2 bpp
   keeps PSNR within 0.25 dB of x264 at ~85 % of its size. Encode cost: 23–28 ms
   per 2560×1120 frame against 288 (PNG) + ~200 (x264) before. */
var SEG_BPP = 0.2;
function _segBitrate(w, h, fps, bpp) {
    return Math.round(Math.max(1500000, Math.min(40000000, w * h * fps * (bpp > 0 ? bpp : SEG_BPP))));
}
function _sameBytes(a, b) {
    if (!a || !b || a.length !== b.length) return false;
    for (var i = a.length - 1; i >= 0; i--) { if (a[i] !== b[i]) return false; }
    return true;
}
function _encLaneError(e) {
    var err = (e instanceof Error) ? e : new Error(String((e && e.message) || e));
    if (err.name === 'AbortError') return err;
    var wrap = new Error('AI_ENCODE_LANE: ' + String(err.message || '').slice(0, 160));
    wrap.__encLane = true;
    wrap.cause = err;
    return wrap;
}
async function _pickEncodeLane(w, h, fps, slug, bpp) {
    var pinned = window.__aiUpscaleEncodeLane;   // test seam: 'ffmpeg' | 'webcodecs'
    var pick;
    if (pinned === 'ffmpeg') pick = { lane: 'ffmpeg', reason: 'pinned' };
    else if (!window.CanvasVideoExport || typeof window.CanvasVideoExport.openStream !== 'function') {
        pick = { lane: 'ffmpeg', reason: 'no-openstream' };
    } else {
        var bitrate = _segBitrate(w, h, fps, bpp);
        try {
            // Open and drop one real stream: codec pick, muxer load and the first
            // configure() happen here, so a device that cannot encode says so
            // before the first frame is computed.
            var probe = await window.CanvasVideoExport.openStream({ width: w, height: h, fps: fps, format: 'mp4', bitrate: bitrate });
            probe.abort();
            pick = { lane: 'webcodecs', reason: pinned === 'webcodecs' ? 'pinned' : 'ok', bitrate: bitrate };
        } catch (e) {
            if (pinned === 'webcodecs') throw e;
            pick = { lane: 'ffmpeg', reason: String((e && e.message) || e).slice(0, 80) };
        }
    }
    _ae(slug, 'ai_encode_lane', { lane: pick.lane, reason: pick.reason, w: w, h: h });
    return pick;
}

/* Named size targets — the person names the SIZE they want («HD 720p»), the
   factor follows from the source. Comment #1914 (15.10.2026, Динара): a 640×280
   YouTube rip — «360p» to its owner — could only become 2560×1120 (×4), and
   her question was «как же 720 из 360?». Every upscaler on the market offers
   720p / 1080p / 4K targets; we offered ×4 and a 4K target that equals ×4 for
   any source up to 540p, i.e. ONE choice.

   «Fits in the box» (landscape 1280×720 / 1920×1080 / 3840×2160, the box turned
   for portrait), not «short side equals»: the 640×280 rip's 720p is 1280×560 —
   short-side would make it 1646×720, wider than any 720p screen. The old
   short-side 4K rule did exactly that to scope films: 1920×800 → 5184×2160.

   Quality is the same honest path for every target: the model always sees the
   FULL frame (×4), the result is reduced afterwards (see the pre-scale block in
   run()). So a smaller target does NOT make the network cheaper — it makes
   the file smaller and the encode cheaper; the copy says exactly that. */
var TARGET_BOXES = { hd720: [1280, 720], fhd1080: [1920, 1080], auto4k: [3840, 2160] };

function targetRatio(srcW, srcH, key) {
    var b = TARGET_BOXES[key];
    if (!b || !srcW || !srcH) return 0;
    var land = srcW >= srcH;
    return Math.min((land ? b[0] : b[1]) / srcW, (land ? b[1] : b[0]) / srcH);
}

function _even(x) { return Math.max(2, 2 * Math.round(x / 2)); }

function computeOutputDims(srcW, srcH, scale) {
    srcW = Number(srcW) || 0;
    srcH = Number(srcH) || 0;
    if (!srcW || !srcH) return { w: 0, h: 0, ratio: 1 };
    /* Любое ЧИСЛОВОЕ значение означает ×4: единственный числовой режим.
       ⚠️ Без этой нормализации устаревший вызов с 2 молча проваливался бы в
       ветку цели и получал совсем другие размеры — тихо, без ошибки. */
    if (typeof scale === 'number') return { w: srcW * 4, h: srcH * 4, ratio: 4 };
    /* Явный размер `{w, h}` — у тула со своим выбором разрешения (/enhance-video:
       селект + потолок тарифа решает ResolutionModule). Сторона ограничена ×4
       (родной выход модели) с сохранением пропорций; цель меньше исходника
       допустима — модель всё равно считает полный кадр, результат уменьшается. */
    if (scale && typeof scale === 'object' && scale.w > 0 && scale.h > 0) {
        var k = Math.min(1, (4 * srcW) / scale.w, (4 * srcH) / scale.h);
        var ew = _even(scale.w * k), eh = _even(scale.h * k);
        return { w: ew, h: eh, ratio: Math.max(ew / srcW, eh / srcH) };
    }
    /* ⛔ scale === 2 УДАЛЁН (решение Фарида 19.08): он уменьшал исходник ДО
       модели и отдавал картинку хуже бесплатного растягивания. Цели ниже
       (720p/1080p/4K) — не его возврат: модель видит полный кадр, уменьшается
       уже результат. */
    var key = TARGET_BOXES[scale] ? scale : 'auto4k';
    /* Не меньше исходника (цель, которая меньше файла, не показывается — см.
       блейд) и не больше ×4 (родной выход модели). Чётные стороны: H.264 4:2:0
       нечётных не кодирует (854×480 → 720p даёт 719.4 px по высоте). */
    var ratio = Math.max(1, Math.min(4, targetRatio(srcW, srcH, key)));
    if (ratio >= 4) return { w: srcW * 4, h: srcH * 4, ratio: 4 };
    var w = _even(srcW * ratio), h = _even(srcH * ratio);
    return { w: w, h: h, ratio: Math.max(w / srcW, h / srcH) };
}

/**
 * Какие размеры кадра реально уходят В МОДЕЛЬ.
 *
 * ⚠️ Одно место на весь тул: оценка времени в блейде считает тайлы по этому же
 * числу. Пока формула жила в двух копиях, они разъезжались — оценка «до 4К»
 * занижалась вчетверо после удаления пред-уменьшения.
 *
 * Обычно это полный кадр. Уменьшение — только когда ×4 от него не влезает в
 * потолок промежуточного кадра, и никогда ниже целевых размеров.
 */
var MAX_INTERMEDIATE_SIDE = 8192;   // тот же потолок, что у серверного лейна

function computeFeedDims(srcW, srcH, scale) {
    srcW = Number(srcW) || 0;
    srcH = Number(srcH) || 0;
    if (!srcW || !srcH) return { w: 0, h: 0 };
    var dims = computeOutputDims(srcW, srcH, scale);
    var longSide4x = Math.max(srcW, srcH) * 4;
    if (longSide4x <= MAX_INTERMEDIATE_SIDE) return { w: srcW, h: srcH };
    var fit = MAX_INTERMEDIATE_SIDE / longSide4x;
    var floorF = Math.max(dims.w / (srcW * 4), dims.h / (srcH * 4));
    var f = Math.max(fit, Math.min(1, floorF));
    return { w: Math.max(1, Math.round(srcW * f)), h: Math.max(1, Math.round(srcH * f)) };
}

async function isSupported() {
    try {
        if (typeof window.AIUpscaleCore !== 'function') return false;
        var caps = window.AIUpscaleCore.checkCapabilities();
        if (!caps || !caps.supported) return false;
        var probeOk = await window.AIUpscaleCore.probe();
        return !!probeOk;
    } catch (_) {
        return false;
    }
}

/**
 * Какую модель грузить: ту, что ВЫБРАЛ человек, иначе — единственную из мета-тегов.
 *
 * ⚠️ Мета-теги остаются рабочим путём: их читают /enhance-video,
 * /photo-enhancer и /photo-restore, у которых выбора моделей нет. Новый путь
 * ДОБАВЛЕН, а не подменяет старый — иначе одна правка тула сломала бы четыре.
 *
 * Возвращает и SHA, и тайл, потому что оба — свойства КОНКРЕТНОЙ модели:
 * чужая сумма не сойдётся и честная загрузка будет выглядеть как порча кэша,
 * а чужой тайл либо отвергнется пробой, либо потеряет скорость.
 */
/* The model the PAGE ships in its <meta> quartet (url / fallback / sha /
   tile — the registry default via upscale_model()). ⚠️ sha and tile are
   part of the address: a core built from the URL alone verifies the bytes
   against its built-in x4plus sum and rejects every other model as
   «checksum mismatch». Until 2026-09-16 this returned sha:null, which was
   only ever right while the meta named x4plus itself. */
function _metaModel() {
    var meta = function (n) { var el = document.querySelector('meta[name=' + n + ']'); return (el && el.content) || ''; };
    var primary = meta('ai-upscale-model'), fallback = meta('ai-upscale-model-fallback');
    var urls = [];
    if (primary) urls.push(primary);
    if (fallback && fallback !== primary) urls.push(fallback);
    return { urls: urls, sha: meta('ai-upscale-model-sha') || null,
             tile: parseInt(meta('ai-upscale-model-tile'), 10) || 0, key: meta('ai-upscale-model-key') || '' };
}
function _resolveModel() {
    var sel = (typeof window !== 'undefined') ? window.__upscaleModel : null;
    if (sel && sel.urls && sel.urls.length) {
        return { urls: [].concat(sel.urls), sha: sel.sha || null, tile: sel.tile || 0, key: sel.key || '' };
    }
    return _metaModel();
}

function _resolveModelUrls() { return _resolveModel().urls; }

async function _probeSource(file) {
    // Universal probe — tries mp4box → HTMLVideoElement → FFmpeg.probeMeta
    // (covers AVI/MKV/WMV/FLV/TS). See public/js/video-probe.js for the
    // canonical implementation. Falls back to the legacy inline ladder
    // if the helper hasn't loaded yet (older deploys, race during a
    // partial cache update). Telemetry consumer reads probe_path from
    // the result so we can size the FFmpeg-tier cohort.
    if (typeof window.__videoProbe === 'function') {
        var r = await window.__videoProbe(file);
        // r === null only when a newer probe call superseded ours (race
        // guard). Surface as no-stream — caller will normally have
        // already moved on, but we don't want to return stale data.
        if (!r || !r.width || !r.height) {
            var err = new Error('AI_NO_VIDEO_STREAM');
            err._userVisible = true;
            err._probePath = (r && r.probe_path) || 'none';
            throw err;
        }
        return {
            width: r.width,
            height: r.height,
            duration: r.duration || 0,
            fps: r.fps || 30,
            codec: r.codec || '',
            codecFull: r.codecFull || '',
            isHdr: !!r.isHdr,
            hdrFormat: r.hdrFormat || null,
            probe_path: r.probe_path,
            probe_ms: r.probe_ms || 0,
            container: r.container || 'unknown'
        };
    }
    // ── LEGACY FALLBACK (video-probe.js not loaded) ─────────────────
    // Shouldn't happen in practice — video-probe.js is loaded before
    // pipeline.js in the blade. Kept for resilience during the rollout
    // window when older cached JS may still be live.
    if (window.WebCodecsPipeline && typeof window.WebCodecsPipeline.probeCodec === 'function') {
        try {
            var mp4Probe = await window.WebCodecsPipeline.probeCodec(file);
            if (mp4Probe && mp4Probe.width && mp4Probe.height) {
                return {
                    width: mp4Probe.width,
                    height: mp4Probe.height,
                    duration: mp4Probe.duration || 0,
                    fps: mp4Probe.fps || 30,
                    codec: mp4Probe.videoCodec || '',
                    codecFull: mp4Probe.videoCodecFull || '',
                    isHdr: !!mp4Probe.isHdr,
                    hdrFormat: mp4Probe.hdrFormat || null,
                    probe_path: 'mp4box-legacy'
                };
            }
        } catch (_) {}
    }
    return new Promise(function (resolve, reject) {
        var url = URL.createObjectURL(file);
        var v = document.createElement('video');
        v.preload = 'metadata';
        v.muted = true;
        var done = false;
        var settle = function (ok, val) {
            if (done) return; done = true;
            URL.revokeObjectURL(url);
            try { v.src = ''; v.load(); } catch (_) {}
            (ok ? resolve : reject)(val);
        };
        v.onloadedmetadata = function () {
            settle(true, {
                width: v.videoWidth || 0, height: v.videoHeight || 0,
                duration: v.duration || 0, fps: 30, isHdr: false,
                probe_path: 'html5-legacy'
            });
        };
        v.onerror = function () { settle(false, new Error('AI_PROBE_FAILED')); };
        setTimeout(function () { settle(false, new Error('AI_PROBE_TIMEOUT')); }, 8000);
        v.src = url;
    });
}

function _bindCancel(signal, onCancel) {
    if (!signal) return function () {};
    if (signal.aborted) { onCancel(); return function () {}; }
    var h = function () { onCancel(); };
    signal.addEventListener('abort', h, { once: true });
    return function () { try { signal.removeEventListener('abort', h); } catch (_) {} };
}

function _throwIfAborted(signal) {
    if (signal && signal.aborted) {
        var e = new Error('AI_CANCELLED');
        e.name = 'AbortError';
        throw e;
    }
}

// Cumulative `-c copy` concat of every completed segment → preview blob.
// Video-only: audio is muxed into the FINAL result, not the preview, to
// keep this fast. Each call costs ~1 FFmpeg exec (remux, no re-encode);
// scales linearly with segment count (typical 5-min video → 60 segments
// → ~1 sec per emit). Throttled at the call site so total overhead is
// a handful of seconds across the whole job.
async function _buildPartial(ffApi, segments, slug) {
    if (!segments || !segments.length) throw new Error('partial_no_segments');
    var concatList = segments.map(function (s) { return "file '" + s + "'"; }).join('\n');
    await ffApi.writeFile('ai_pcat.txt', new TextEncoder().encode(concatList));
    var rc = await window.FFmpegProcessor.execGuarded(
        ['-f', 'concat', '-safe', '0', '-i', 'ai_pcat.txt',
         '-c:v', 'copy', '-movflags', '+faststart', '-y', 'ai_preview.mp4'],
        { softMs: 30000, hardMs: 90000, stage: 'ai_partial_concat',
          meta: { slug: slug, segments: segments.length } }
    );
    if (rc !== 0) throw new Error('partial_concat_rc_' + rc);
    var bytes = await ffApi.readFile('ai_preview.mp4');
    try { await ffApi.deleteFile('ai_preview.mp4'); } catch (_) {}
    try { await ffApi.deleteFile('ai_pcat.txt'); } catch (_) {}
    // Detach from MEMFS-backed view — Blob keeps its own copy via slice.
    var copy = bytes && bytes.slice ? bytes.slice() : new Uint8Array(bytes);
    return new Blob([copy], { type: 'video/mp4' });
}

async function _downscaleCanvas(srcCanvas, targetW, targetH) {
    // High-quality canvas downscale. browsers' default smoothing is bilinear;
    // good enough for the small downscale step after Real-ESRGAN ×4 output.
    if (srcCanvas.width === targetW && srcCanvas.height === targetH) return srcCanvas;
    var out = document.createElement('canvas');
    out.width = targetW; out.height = targetH;
    var ctx = out.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(srcCanvas, 0, 0, targetW, targetH);
    return out;
}

/* Кадр, стёртый сбросом видеокарты (см. цикл кадра): спрашиваем общий примитив.
   Нет примитива — не решаем (false): работу не останавливаем на догадке. */
function _frameEmptied(canvas) {
    try { return !!(window.PixelProof && window.PixelProof.emptied && window.PixelProof.emptied(canvas)); }
    catch (_) { return false; }
}

/* Стёртый кадр считается заново, но не бесконечно: после сброса карта
   оживает за доли секунды (замер 30.10 — следующий кадр уже цел), поэтому
   пауза растёт, а третий стёртый подряд — типизированный отказ: лучше честная
   ошибка, чем ролик с чёрными кадрами. */
async function _frameRedoPause(slug, at, attempt, lane) {
    _ae(slug, 'ai_frame_redone', { at: at, attempt: attempt + 1, lane: lane });
    if (attempt >= 2) {
        throw Object.assign(new Error('AI_FRAME_LOST: the ' + at + ' frame read back empty three times (GPU reset)'), { code: 'AI_FRAME_LOST' });
    }
    var ms = attempt === 0 ? 300 : 1200;
    await new Promise(function (r) { (window.__visibleTimeout || setTimeout)(r, ms); });
}

/* The tonemap the run applies to an HDR source before the model sees a frame —
   one owner for run() and sample(), so a sample frame is the run's frame. */
async function _hdrTonemapArgs(ffApi, probe, slug) {
    // Has the FFmpeg-WASM build got libzimg compiled in? The standard
    // @ffmpeg/core@0.12.10 build does NOT include it, which means the
    // zscale HDR→SDR chain throws "No such filter"
    // → rc != 0 → AI_CHUNK_EXTRACT_FAILED → user gets a generic error.
    // We probe once per session by running `-filters` and grep'ing
    // the log output for `zscale`. Result cached on the FFmpegProcessor
    // namespace so subsequent runs (batch / re-run) skip the probe.
    if (typeof window.FFmpegProcessor._hdrTonemapCapable === 'undefined') {
        try {
            var filterLog = '';
            var logCb = function (ev) {
                if (ev && ev.message) filterLog += ev.message + '\n';
            };
            if (ffApi.on) ffApi.on('log', logCb);
            try { await ffApi.exec(['-filters']); } catch (_) {}
            if (ffApi.off) ffApi.off('log', logCb);
            window.FFmpegProcessor._hdrTonemapCapable = /\bzscale\b/i.test(filterLog);
            _ae(slug, 'ai_hdr_capability_probed', {
                capable: !!window.FFmpegProcessor._hdrTonemapCapable
            });
        } catch (_) {
            window.FFmpegProcessor._hdrTonemapCapable = false;
        }
    }
    var hdrTonemapOk = !!window.FFmpegProcessor._hdrTonemapCapable;
    // HDR sources tonemap to SDR bt709 before PNG encode — ESRGAN is
    // SDR-trained. Tonemap only runs when the source IS HDR AND the
    // FFmpeg-WASM build actually has libzimg compiled in (probe'd
    // above). Without zimg the tonemap chain throws "No such filter"
    // and breaks extract; better to ship slightly desaturated /
    // clipped output than fail outright. Telemetry below records the
    // mismatch so we know how many HDR sources hit the no-tonemap path.
    // The chain is the FFmpeg half of the HDR → SDR door — one string for
    // every FFmpeg lane (CodecMuxRules.hdrToSdrFilter, docs/hdr-to-sdr.md).
    var hdrVf = (window.CodecMuxRules && typeof window.CodecMuxRules.hdrToSdrFilter === 'function')
        ? window.CodecMuxRules.hdrToSdrFilter() : '';
    var doTonemap = probe.isHdr && hdrTonemapOk && !!hdrVf;
    var tonemapVf = doTonemap ? ['-vf', hdrVf] : [];
    return tonemapVf;
}

/* ── Лица на видео (28.10.2026) ───────────────────────────────────────────────
   Опция человека: после увеличения кадра нейросеть восстанавливает лица (общий
   движок FaceRestoreEngine, RestoreFormer++, видео-трекер со сглаживанием во
   времени). Лица ищет FRLandmarks (MediaPipe) на УВЕЛИЧЕННОМ кадре. Пять точек
   FFHQ — уголки глаз (средние), кончик носа, уголки рта из 478 меток; порядок —
   слева направо по кадру, как в шаблоне движка. Замеры и пределы —
   docs/why/video-face-restore.md. */
var _FACE5 = { eyeA: [33, 133], eyeB: [362, 263], nose: 1, mouthA: 61, mouthB: 291 };
function _facePoints5(pts, W, H) {
    if (!pts || pts.length < 300) return null;
    function at(i) { return [pts[i].x * W, pts[i].y * H]; }
    function mid(a, b) { var p = at(a), q = at(b); return [(p[0] + q[0]) / 2, (p[1] + q[1]) / 2]; }
    var e1 = mid(_FACE5.eyeA[0], _FACE5.eyeA[1]), e2 = mid(_FACE5.eyeB[0], _FACE5.eyeB[1]);
    var m1 = at(_FACE5.mouthA), m2 = at(_FACE5.mouthB);
    if (e1[0] > e2[0]) { var t = e1; e1 = e2; e2 = t; }
    if (m1[0] > m2[0]) { var u = m1; m1 = m2; m2 = u; }
    return [e1, e2, at(_FACE5.nose), m1, m2];
}
async function _openFaceLane(cfg, slug, signal, onProgress) {
    var E = window.FaceRestoreEngine, L = window.FRLandmarks;
    if (!E || typeof E.createVideoTracker !== 'function' || !L) throw Object.assign(new Error('AI_FACES_UNAVAILABLE: modules'), { code: 'AI_FACES_UNAVAILABLE' });
    var t0 = Date.now();
    if (cfg.ort) E.configure({ ort: cfg.ort });
    var handle = await E.createWorkerSession(cfg.modelKey || 'rfpp', cfg.modelUrls || [], {
        preferWebGPU: true, signal: signal,
        onProgress: function (frac, got, total) { try { onProgress({ phase: 'faces_model', done: got || 0, total: total || 0, frac: frac || 0 }); } catch (_) {} }
    });
    if (!handle || handle.provider !== 'webgpu') {
        try { E.resetWorker && E.resetWorker(); } catch (_) {}
        throw Object.assign(new Error('AI_FACES_UNAVAILABLE: no card'), { code: 'AI_FACES_UNAVAILABLE' });
    }
    await L.ensure(cfg.sources || [], { numFaces: 4, mode: 'IMAGE', downloaderUrl: cfg.downloaderUrl });
    _ae(slug, 'ai_faces_ready', { ms: Date.now() - t0, provider: handle.provider || '' });
    return { handle: handle, tracker: E.createVideoTracker({}), ms: 0, frames: 0, found: 0, restored: 0 };
}
function _closeFaceLane(lane) {
    if (!lane) return;
    try { window.FaceRestoreEngine && window.FaceRestoreEngine.resetWorker && window.FaceRestoreEngine.resetWorker(); } catch (_) {}
    try { window.FRLandmarks && window.FRLandmarks.close && window.FRLandmarks.close(); } catch (_) {}
}

async function run(opts) {
    opts = opts || {};
    if (!opts.file) throw new Error('AI_NO_FILE');
    var slug = opts.slug || DEFAULT_SLUG;
    var scale = opts.scale || 4;
    var signal = opts.signal || null;
    var onProgress = typeof opts.onProgress === 'function' ? opts.onProgress : function () {};

    if (typeof window.AIUpscaleCore !== 'function') throw new Error('AI_CORE_NOT_LOADED');
    if (!window.FFmpegProcessor) throw new Error('AI_FFMPEG_NOT_LOADED');

    var modelUrls = _resolveModelUrls();
    if (!modelUrls.length) throw new Error('AI_NO_MODEL_URL');

    /* Тул, который уже знает файл (размеры, длину, частоту, HDR), отдаёт свою
       пробу — второй проход по файлу ему не нужен. */
    var probe = (opts.probe && opts.probe.width && opts.probe.height)
        ? Object.assign({ fps: 30, duration: 0, codec: '', isHdr: false, probe_path: 'caller', container: 'unknown' }, opts.probe)
        : await _probeSource(opts.file);
    _throwIfAborted(signal);
    if (!probe.width || !probe.height) {
        throw Object.assign(new Error('AI_NO_VIDEO_STREAM'), { _userVisible: true });
    }

    // Wave 24 — lowMem (2-3 GB) devices run AI only on small sources.
    // Enforced HERE (before model download / WebGPU session) so every
    // consumer — single file, batch queue, future tools — hits one gate.
    // The blades additionally warn at pick time; this is the backstop.
    var _caps = window.AIUpscaleCore.checkCapabilities();
    if (_caps && _caps.lowMem) {
        var _srcPx = probe.width * probe.height;
        var _pxCap = window.AIUpscaleCore.LOWMEM_SRC_PX || 307200;
        if (_srcPx > _pxCap) {
            _ae(slug, 'ai_lowmem_input_blocked', {
                src_w: probe.width, src_h: probe.height,
                dm: navigator.deviceMemory || 0
            });
            var _lmErr = new Error('AI_LOW_MEM_INPUT');
            _lmErr._userVisible = true;
            throw _lmErr;
        }
        _ae(slug, 'ai_lowmem_allowed', {
            src_w: probe.width, src_h: probe.height,
            dm: navigator.deviceMemory || 0
        });
    }

    var dims = computeOutputDims(probe.width, probe.height, scale);
    /* ⚠️⚠️⚠️ ЗДЕСЬ БЫЛО ПРЕД-УМЕНЬШЕНИЕ, И ОНО ПОРТИЛО КАРТИНКУ.
       Модель фиксированно ×4, поэтому для цели меньше ×4 исходник уменьшался
       ДО инференса (`dims/4`), «чтобы считать в 4 раза быстрее». Скорость там
       была посчитана, качество — нет. Замер 2026-08-19 (ORT-CPU, фотография
       Kodak kodim19, забор из планок, исходник 256 → цель ×2):

         уменьшить→×4 (как было)      PSNR 21.80  SSIM 0.6555  краёв 0.037
         ×4 на полном→уменьшить       PSNR 26.12  SSIM 0.8504  краёв 0.062
         Lanczos вообще без модели     PSNR 26.89  SSIM 0.8715  краёв 0.046
                                       (эталон краёв 0.076)

       То есть режим ×2 был ХУЖЕ, чем не делать ничего: проигрывал обычному
       растягиванию 5 dB, а на снимке забор исчезал целиком, оставляя гладкую
       стену. Контроль на вдвое большем исходнике: 25 прогонов модели давали
       ровно ту же детальность, что бесплатный ресайз.

       Теперь модель ВСЕГДА получает полный кадр, а до целевых размеров
       результат доводится уменьшением ПОСЛЕ инференса (ниже уже был такой
       проход — он был написан как «страховка на 1 px», а стал основным).
       Цена: инференс дороже в (4/ratio)² раз. Это осознанный размен, решение
       Фарида 19.08 — вместе с удалением самого пункта ×2 из выбора.
       Разбор: память reference_x2_prescale_destroys_detail_2026_08_19.

       ⚠️ То же решение и с тем же обоснованием уже жило в `/photo-enhancer`
       (`planUpscale`): «feeding it a half-size copy throws away three quarters
       of the real pixels before inference». Знание было в соседнем туле и не
       доехало сюда — поэтому здесь оно записано явно, а не ссылкой.

       ⚠️⚠️ Но у соседа есть и ЗАЩИТА ПО ПАМЯТИ, и без неё эта правка опасна:
       промежуточный кадр теперь всегда `исходник×4`. Для 4К-исходника с целью
       «до 4К» (ratio 1.5) это 15360×8640 — вчетверо больше цели и заведомо за
       пределами canvas. Поэтому кормим полным кадром ТОЛЬКО пока ×4 от него
       влезает в потолок; иначе уменьшаем ровно настолько, насколько вынуждает
       потолок, и НИКОГДА ниже целевых размеров (ниже цели пришлось бы потом
       растягивать — это вернуло бы ту самую потерю). */
    var feed = computeFeedDims(probe.width, probe.height, scale);
    var preDownscale = feed.w < probe.width || feed.h < probe.height;
    var preW = feed.w, preH = feed.h;

    // Adaptive chunk size — drops 5s → 2s @ 4K, → 1s @ 8K to keep MEMFS
    // staging below the WASM 4 GB hard wall. See pickChunkSec() math.
    var chunkSec = pickChunkSec(dims.w, dims.h);

    var aiStart = Date.now();
    _ae(slug, 'ai_upscale_start', {
        scale: (scale && typeof scale === 'object') ? 'size' : scale,
        src_w: probe.width, src_h: probe.height,
        out_w: dims.w, out_h: dims.h,
        duration: Math.round(probe.duration || 0),
        fileMB: Math.round(opts.file.size / 1048576),
        cores: navigator.hardwareConcurrency || 0,
        dm: navigator.deviceMemory || 0,
        chunk_sec: chunkSec,                          // 5 default, 2 @4K, 1 @8K
        is_hdr: !!probe.isHdr,
        probe_path: probe.probe_path || 'unknown',    // mp4box|html5|ffmpeg
        probe_ms: probe.probe_ms || 0,                // tier-ladder cost
        container: probe.container || 'unknown',      // mp4|webm|avi|wmv|flv|ts|ogg|empty|unknown
        src_codec: (probe.codec || '').slice(0, 12),
        // Adapter identity captured during cap_probe — lets us crosstab
        // failures × hardware (Intel iGPU vs NVIDIA discrete vs Apple etc.)
        gpu_vendor: ((window.AIUpscaleCore && window.AIUpscaleCore.lastAdapterInfo && window.AIUpscaleCore.lastAdapterInfo.vendor) || '').slice(0, 16),
        gpu_architecture: ((window.AIUpscaleCore && window.AIUpscaleCore.lastAdapterInfo && window.AIUpscaleCore.lastAdapterInfo.architecture) || '').slice(0, 24)
    });

    onProgress({ phase: 'model_load', done: 0, total: 100 });

    // STEP 1 — core init (download + verify + compile).
    /* ⚠️ Выбранная модель может не доехать: файлы моделей лежат ВНЕ git и вне
       деплоя (`public/vendor/onnx/` в .gitignore, nginx отдаёт их из
       /var/www/static/), поэтому забытая выкладка даёт 404 ровно на новой
       записи реестра. Пока модель была одна, такой промах ломал тул целиком и
       это было честно. Со списком — нет: человек выбрал пункт, который мы ему
       предложили, и остаться без инструмента он не должен. Поэтому при отказе
       мы ОДИН раз возвращаемся к модели из мета-тега — той, что живёт здесь
       месяцами и имеет внешнее зеркало, — и говорим об этом в телеметрию.
       Тише, но работает; молча подменять на КАЖДОМ отказе нельзя, иначе
       сломанная запись реестра никогда не всплывёт. */
    var _model = _resolveModel();
    function _makeCore(m) {
        return new window.AIUpscaleCore({
            modelUrls: m.urls && m.urls.length ? m.urls : modelUrls,
            expectedSha: m.sha,
            modelTile: m.tile,
            slug: slug
        });
    }
    var core = _makeCore(_model);
    var disposed = false;
    var faceLane = null;
    var unbindCancel = _bindCancel(signal, function () {
        try { core.requestCancel(); } catch (_) {}
    });
    var _initProgress = function (p) {
                if (p.phase === 'download') {
                    var pct = p.total ? Math.round((p.loaded / p.total) * 18) : 0;
                    onProgress({ phase: 'model_load', done: pct, total: 100 });
                } else if (p.phase === 'verify') {
                    onProgress({ phase: 'compile', done: 20, total: 100 });
                } else if (p.phase === 'compile') {
                    onProgress({ phase: 'compile', done: 22, total: 100 });
                }
    };
    try {
        try {
            await core.init({ onProgress: _initProgress });
        } catch (modelErr) {
            // Отказ ИМЕННО выбранной записи (404 / несовпавшая сумма) — один
            // возврат к модели из мета-тега. Всё остальное (нет WebGPU, отмена)
            // пробрасываем как раньше: подменять модель там нечего.
            var kind = (modelErr && modelErr.code) || '';
            var recoverable = (kind === 'model_corrupt' || /fetch|404|network/i.test(String(modelErr && modelErr.message || '')));
            /* The fallback is the page's META model — a DIFFERENT entry from
               the one that just failed, with its own sha and tile. ⚠️ Until
               2026-09-16 this retried the SAME urls with sha:null, i.e. the
               selected model checked against the x4plus sum: for any registry
               entry but x4plus the «fallback» failed the way the first try did. */
            var _meta = _metaModel();
            var haveFallback = !!(_model.key && _meta.urls.length && _meta.urls[0] !== (_model.urls && _model.urls[0]));
            if (!recoverable || !haveFallback) throw modelErr;
            try { window.__ae && window.__ae('tool_error', {
                slug: 'video-upscaler', action: 'model_fallback',
                model: _model.key, to: _meta.key, err: String(modelErr && modelErr.message || '').slice(0, 120)
            }); } catch (_) {}
            try { core.dispose && core.dispose(); } catch (_) {}
            core = _makeCore(_meta);
            await core.init({ onProgress: _initProgress });
        }
        _throwIfAborted(signal);

        // GPU calibration handshake — the worker's 2nd warm probe run
        // measured THIS device's steady-state per-tile inference cost.
        // Surface it to the UI before any real frame so the blade can
        // replace its 80 ms/tile desktop heuristic with reality and
        // re-render the button/cap ETA. Closes bug #alarionov93
        // ("5 h on the button, 26 h in the progress bar").
        if (core.tileMsMeasured > 0) {
            onProgress({
                phase: 'gpu_calibrated',
                tileMs: core.tileMsMeasured,
                sessionCreateMs: core.sessionCreateMs || 0,
                kernelProbeMs: core.kernelProbeMs || 0
            });
            _ae(slug, 'ai_gpu_calibrated', {
                tile_ms: core.tileMsMeasured,
                session_create_ms: core.sessionCreateMs || 0,
                kernel_probe_ms: core.kernelProbeMs || 0,
                gpu_vendor: ((window.AIUpscaleCore && window.AIUpscaleCore.lastAdapterInfo && window.AIUpscaleCore.lastAdapterInfo.vendor) || '').slice(0, 16)
            });
        }

        /* Устройство только что измерило себя — до первого кадра. Тул может здесь
           спросить человека («это займёт 70 минут — продолжить?»): хук ждётся,
           его исключение заканчивает прогон (ядро освобождается в finally). */
        if (typeof opts.afterCalibrate === 'function') {
            await opts.afterCalibrate({ tileMs: core.tileMsMeasured || 0, dims: dims, feed: feed });
            _throwIfAborted(signal);
        }
        if (opts.faces) {
            faceLane = await _openFaceLane(opts.faces, slug, signal, onProgress);
            _throwIfAborted(signal);
        }

        // STEP 2 — FFmpeg init + audio extract.
        onProgress({ phase: 'extract', done: 23, total: 100 });
        /* ⚠️ initWithProgress ПОДПИСЫВАЕТ этот коллбэк на прогресс КАЖДОГО
           следующего exec движка (фаза 'proc', правка #1518 для тулов, у
           которых обработка — один exec). Здесь exec'ов сотни — извлечение и
           сборка каждого фрагмента, — и все они рисовались как «Извлечение
           кадров…» на 23–25 %: полоса откатывалась с 47/68/90 % к 24 % на
           каждой границе фрагмента и стояла там 40–50 с (замер 15.10.2026,
           #1914). Свою шкалу конвейер ведёт по кадрам — движку здесь доверена
           только его собственная загрузка. */
        var initOk = await window.FFmpegProcessor.initWithProgress(function (ph, pct) {
            if (ph !== 'load') return;
            onProgress({ phase: 'extract', done: 23 + pct * 0.02, total: 100 });
        }, { loading: 'AI', processing: 'AI' });
        if (!initOk) throw new Error('AI_FFMPEG_INIT_FAILED');
        var ffApi = window.FFmpegProcessor._getFFmpeg && window.FFmpegProcessor._getFFmpeg();
        if (!ffApi || typeof ffApi.exec !== 'function') throw new Error('AI_FFMPEG_RAW_UNAVAILABLE');

        // Clamp fps for extraction. Cap at 60 — historic cap was 30 which
        // halved temporal info for 60 fps gaming / sports / phone HFR
        // source. The cost: 60 fps doubles inference count per second of
        // content, ~2× wall-time vs 30 fps cap. Tradeoff favoured fidelity
        // since user opted in to AI upscale (high-quality intent) and
        // temporal halving is visible as judder on the very content
        // (motion-heavy) that benefits most from upscale. Telemetry
        // tracks the source-vs-extract delta so we can refine if a
        // cohort cancels because "it's twice as slow as before".
        var srcFps = Number(probe.fps) || 30;
        var extractFps = Math.max(1, Math.min(srcFps, opts.maxFps > 0 ? opts.maxFps : 60));
        if (srcFps > extractFps) {
            _ae(slug, 'ai_fps_clamped', { src_fps: Math.round(srcFps), out_fps: extractFps });
        }
        var tonemapVf = await _hdrTonemapArgs(ffApi, probe, slug);
        if (probe.isHdr && !tonemapVf.length) {
            _ae(slug, 'ai_hdr_tonemap_unavailable', {
                hdr_format: probe.hdrFormat || 'unknown',
                src_codec: (probe.codec || '').slice(0, 16)
            });
        }

        var mountHandle = await window.FFmpegProcessor.mountInput(opts.file);
        var inputPath = mountHandle.path;
        var hasAudio = false;
        var segments = [];
        var totalFrames = 0;
        var dupFrames = 0, _prevIn = null, _prevOut = null;
        var frameMs = [];
        var estChunks = Math.max(1, Math.ceil((probe.duration || chunkSec) / chunkSec));
        var estTotal = Math.max(1, Math.round((probe.duration || chunkSec) * extractFps));
        // Throttle state for the streaming partial-preview emit.
        var onPartialReady = typeof opts.onPartialReady === 'function' ? opts.onPartialReady : null;
        var lastPartialWall = 0;
        var lastPartialContentSec = 0;
        var partialEmitCount = 0;

        try {
            // Audio passthrough — extract once for the whole clip.
            /* ⚠️ Страница обещала «звук переносится без перекодирования и без
               потерь», а звук кодировался в AAC 128k ДВАЖДЫ — здесь и ещё раз
               при сборке. AAC (так у большинства MP4) теперь копируется как
               есть и при извлечении, и при сборке; другой кодек — один раз в
               AAC 128k, потому что MP4-результату нужен AAC. */
            var audioCopied = false;
            var _extractAudio = async function (args) {
                var rc = await window.FFmpegProcessor.execGuarded(
                    ['-i', inputPath, '-vn'].concat(args).concat(['-y', 'ai_audio.aac']),
                    { softMs: 60000, hardMs: 300000, stage: 'ai_extract_audio', meta: { slug: slug } }
                );
                if (rc !== 0) return false;
                try { var ab = await ffApi.readFile('ai_audio.aac'); return !!(ab && ab.length > 64); }
                catch (_) { return false; }
            };
            try {
                hasAudio = audioCopied = await _extractAudio(['-c:a', 'copy']);
                if (!hasAudio) hasAudio = await _extractAudio(['-c:a', 'aac', '-b:a', '128k']);
            } catch (_) {}
            _throwIfAborted(signal);

            // STEP 3 — chunked extract → upscale → encode loop.
            /* Кодер фрагментов выбирается ОДИН раз на прогон: аппаратный H.264
               (CanvasVideoExport.openStream — общий примитив «кадры рождаются по
               одному и долго»), иначе прежний PNG → libx264 в FFmpeg-WASM. */
            var encLane = await _pickEncodeLane(dims.w, dims.h, extractFps, slug, opts.segBpp);
            var segStream = null;
            var maxChunks = estChunks + 4;     // small headroom for probe-imprecise duration
            for (var ci = 0; ci < maxChunks; ci++) {
                _throwIfAborted(signal);
                var chunkArgs = ['-ss', String(ci * chunkSec), '-i', inputPath, '-t', String(chunkSec)]
                    .concat(tonemapVf)
                    .concat(['-r', String(extractFps), '-fps_mode', 'cfr', '-y', 'ai_in_%05d.png']);
                var exRc = await window.FFmpegProcessor.execGuarded(chunkArgs, {
                    softMs: 60000, hardMs: 600000, stage: 'ai_chunk_extract',
                    meta: { slug: slug, chunk: ci }
                });
                if (exRc !== 0) throw new Error('AI_CHUNK_EXTRACT_FAILED chunk=' + ci);

                var entries = await ffApi.listDir('/');
                var frames = entries.map(function (e) { return e.name || e; })
                                    .filter(function (n) { return /^ai_in_\d{5}\.png$/.test(n); })
                                    .sort();
                if (!frames.length) break;          // past end of source — done.

                var _frames = frames, _ci = ci;
                var _doChunk = async function () {
                    for (var fi = 0; fi < _frames.length; fi++) {
                        _throwIfAborted(signal);
                        var t0 = performance.now(), faceMsFrame = 0;
                        var pngBytes = await ffApi.readFile(_frames[fi]);
                        // Empty / tiny PNG = FFmpeg silently failed to decode
                        // that frame (HEVC source in MKV / corrupt sample /
                        // EOF mid-extract). Without this guard, the empty
                        // blob makes Image().src fail with a generic
                        // "AI_FRAME_DECODE" — no clue what really broke.
                        if (!pngBytes || pngBytes.length < 100) {
                            var emptyErr = new Error('AI_FRAME_EMPTY: FFmpeg returned ' + (pngBytes ? pngBytes.length : 0) + ' bytes for ' + _frames[fi] + ' (chunk ' + _ci + ', frame ' + fi + ', src=' + (probe.codec || '?') + ')');
                            emptyErr.code = 'AI_FRAME_EMPTY';
                            throw emptyErr;
                        }
                        /* Кадр, побайтно равный предыдущему (стоящая сцена,
                           слайд, заставка, повтор кадра в записи с плавающей
                           частотой), сеть уже считала: модель детерминирована,
                           ответ тот же. Сравниваются PNG от FFmpeg, то есть
                           декодированные пиксели; «почти равные» НЕ склеиваются —
                           это не оценка, а тождество. */
                        var dupOut = (_prevIn && _sameBytes(pngBytes, _prevIn)) ? _prevOut : null;
                        var blob = dupOut ? null : new Blob([pngBytes], { type: 'image/png' });
                        // createImageBitmap is ~25-30% faster than the
                        // Image()+blob-URL roundtrip per frame because it
                        // skips the URL → fetch → Image-element decode dance
                        // (the browser decodes directly off the blob bytes).
                        // For 3000+ frame jobs that's minutes saved. Also
                        // makes the URL.revokeObjectURL race impossible —
                        // there's no URL to revoke late.
                        var _frameName = _frames[fi];
                        var img;
                        var _useBitmap = (typeof createImageBitmap === 'function');
                        var url = null;
                        /* Кадр считается из PNG-байтов FFmpeg — они в памяти JS, и
                           сбросить их нечему. Холсты на пути (вход модели, выход,
                           уменьшение) лежат в видеопамяти, и сброс GPU-процесса
                           отдаёт их ПУСТЫМИ, (0,0,0,0) — замер 30.10
                           (tests/playwright/gpu-reset-canvas-probe.mjs, настоящая
                           карта): каждый сброс портит кадр, бывший в этот миг на
                           холсте. Кадр видео непрозрачен всегда, поэтому прозрачный
                           насквозь — стёртый холст, а не чёрная сцена (у той альфа
                           255). Такой кадр не уходит в ролик чёрным: его считают
                           заново, отсюда и функция. Разбор —
                           docs/why/primitives/pixel-proof.md. */
                        var _renderFrame = async function () {
                            if (!blob) blob = new Blob([pngBytes], { type: 'image/png' });
                            if (img && typeof img.close === 'function') { try { img.close(); } catch (_) {} }
                            img = null;
                            if (_useBitmap) {
                                try {
                                    img = await createImageBitmap(blob);
                                } catch (cbErr) {
                                    var e1 = new Error('AI_FRAME_DECODE: createImageBitmap failed on ' + _frameName + ' (' + pngBytes.length + ' bytes, chunk ' + _ci + ', frame ' + fi + '): ' + (cbErr && cbErr.message || cbErr));
                                    e1.code = 'AI_FRAME_DECODE';
                                    throw e1;
                                }
                            } else {
                                if (!url) url = URL.createObjectURL(blob);
                                img = await new Promise(function (res, rej) {
                                    var im = new Image();
                                    im.onload = function () { res(im); };
                                    im.onerror = function () {
                                        var e2 = new Error('AI_FRAME_DECODE: Image() failed on ' + _frameName + ' (' + pngBytes.length + ' bytes, chunk ' + _ci + ', frame ' + fi + ')');
                                        e2.code = 'AI_FRAME_DECODE';
                                        rej(e2);
                                    };
                                    im.src = url;
                                });
                            }
                            // ImageBitmap отдаёт .width/.height, <img> —
                            // .naturalWidth/Height, поэтому объединение.
                            var srcW = img.naturalWidth || img.width;
                            var srcH = img.naturalHeight || img.height;
                            var oc = null;
                            for (var ia = 0; ; ia++) {
                                var inC = document.createElement('canvas');
                                // Полный кадр — обычный случай. Уменьшенный —
                                // ТОЛЬКО когда ×4 от полного не влезает в потолок
                                // промежуточного кадра (см. блок выше); тогда
                                // уменьшаем ровно на столько, сколько вынуждает
                                // потолок, но не ниже цели.
                                if (preDownscale) {
                                    inC.width = preW; inC.height = preH;
                                    var inCtx = inC.getContext('2d');
                                    inCtx.imageSmoothingEnabled = true;
                                    inCtx.imageSmoothingQuality = 'high';
                                    inCtx.drawImage(img, 0, 0, preW, preH);
                                } else {
                                    /* Холст «часто читаю» с рождения: ядро читает
                                       кадр целиком, и такой холст отдаёт его без
                                       чтения из видеопамяти — 1080p 12 → 7 мс, и
                                       сброс GPU его не стёр ни разу из 6 прогонов
                                       (обычный — 4 кадра). Уменьшению флаг не дан:
                                       там он давал провалы до 0,7 с (замер 30.10). */
                                    inC.width = srcW; inC.height = srcH;
                                    inC.getContext('2d', { willReadFrequently: true }).drawImage(img, 0, 0);
                                }
                                try {
                                    // onTileProgress feeds the stall-guard a tick per
                                    // TILE (~50-350 ms cadence) instead of per FRAME.
                                    // Pre-fix the only stall-tick fired after a whole
                                    // frame — an 8K frame on a slow GPU takes 200+ s,
                                    // tripping the 120 s soft-warn on EVERY frame
                                    // (459 false "Encoding is slow… click Cancel"
                                    // toasts / 27 sessions / 30 d). Real stalls
                                    // (device-lost, driver hang) still trip: tiles
                                    // stop ticking entirely.
                                    oc = await core.upscaleFrame(inC, {
                                        opaque: true,
                                        onTileProgress: function () {
                                            try { window.dispatchEvent(new Event('stall-tick')); } catch (_) {}
                                        }
                                    });
                                    break;
                                } catch (upErr) {
                                    if (!upErr || upErr.code !== 'input_lost') throw upErr;
                                    await _frameRedoPause(slug, 'input', ia, encLane.lane);
                                }
                            }
                            // Приведение к целевым размерам. Раньше это была
                            // «страховка на 1 px» (пред-уменьшение уже давало
                            // нужный размер), теперь это ОСНОВНОЙ путь для
                            // любой цели меньше ×4.
                            if (oc.width !== dims.w || oc.height !== dims.h) {
                                oc = await _downscaleCanvas(oc, dims.w, dims.h);
                            }
                            if (faceLane) {
                                var tF = performance.now();
                                var lm = await window.FRLandmarks.detect(oc, oc.width, oc.height, {});
                                var f5 = [];
                                for (var li = 0; li < (lm || []).length; li++) {
                                    var p5 = _facePoints5(lm[li].pts, oc.width, oc.height);
                                    if (p5) f5.push({ p5: p5 });
                                }
                                var fr = await faceLane.tracker.frame(oc, f5, faceLane.handle, signal);
                                var fMs = performance.now() - tF;
                                faceMsFrame += fMs;
                                faceLane.ms += fMs; faceLane.frames++;
                                faceLane.found += f5.length; faceLane.restored += fr.restored;
                            }
                            return oc;
                        };
                        try {
                            /* Повтор кадра берёт холст прошлого кадра — тот живёт,
                               пока сцена стоит (слайд, заставка: секунды и минуты).
                               Стёртый сбросом, он отдавал бы чёрное до конца
                               стоящей сцены. Холст нужен кодеру всегда, а
                               FFmpeg-лейну — только без готовых PNG-байтов. */
                            var _canvasIsOutput = function () { return encLane.lane === 'webcodecs' || !(dupOut && dupOut.buf); };
                            if (dupOut && !(_canvasIsOutput() && _frameEmptied(dupOut.canvas))) {
                                outC = dupOut.canvas;
                                dupFrames++;
                            } else {
                                if (dupOut) { await _frameRedoPause(slug, 'repeat', 0, encLane.lane); dupOut = null; }
                                var outC = await _renderFrame();
                            }
                            for (var oa = 0; _canvasIsOutput() && _frameEmptied(outC); oa++) {
                                await _frameRedoPause(slug, 'output', oa, encLane.lane);
                                dupOut = null;
                                outC = await _renderFrame();
                            }
                            if (encLane.lane === 'webcodecs') {
                                /* Кадр уходит прямо в аппаратный кодер. Входной
                                   PNG живёт до конца фрагмента: если кодер умрёт,
                                   фрагмент переделывается из него прежним лейном. */
                                try {
                                    if (!segStream) segStream = await window.CanvasVideoExport.openStream({
                                        width: dims.w, height: dims.h, fps: extractFps, format: 'mp4', bitrate: encLane.bitrate });
                                    await segStream.addFrame(outC);
                                } catch (encErr) { throw _encLaneError(encErr); }
                            } else {
                                /* writeFile ОТДАЁТ буфер движку (transfer) — после записи
                                   наш массив пуст. Повтору кадра нужна своя копия. */
                                var outBuf = (dupOut && dupOut.buf) ? dupOut.buf.slice() : null;
                                if (!outBuf) {
                                    var outBlob = await new Promise(function (res, rej) {
                                        outC.toBlob(function (b) {
                                            if (b) res(b);
                                            else rej(new Error('AI_FRAME_ENCODE'));
                                        }, 'image/png');
                                    });
                                    outBuf = new Uint8Array(await outBlob.arrayBuffer());
                                }
                                var keepBuf = outBuf.slice();
                                await ffApi.writeFile(_frames[fi].replace('ai_in_', 'ai_out_'), outBuf);
                                try { await ffApi.deleteFile(_frames[fi]); } catch (_) {}
                            }
                            _prevIn = pngBytes;
                            _prevOut = { canvas: outC, buf: encLane.lane === 'webcodecs' ? null : keepBuf };
                        } finally {
                            // Only revoke if Image() path was used (a URL was
                            // created). createImageBitmap path has no URL.
                            if (url) URL.revokeObjectURL(url);
                            // Free the GPU bitmap explicitly when supported —
                            // ImageBitmap holds GPU memory until close() or GC.
                            // For 3000+ frame jobs, eager close prevents drift.
                            if (img && typeof img.close === 'function') {
                                try { img.close(); } catch (_) {}
                            }
                        }
                        frameMs.push(performance.now() - t0 - faceMsFrame);
                        totalFrames++;
                        var avg = frameMs.reduce(function (a, b) { return a + b; }, 0) / frameMs.length;
                        var remain = Math.max(0, estTotal - totalFrames);
                        var faceAvg = (faceLane && faceLane.frames) ? faceLane.ms / faceLane.frames : 0;
                        var etaSec = Math.round((avg + faceAvg) * remain / 1000);
                        var frac = (_ci + (fi + 1) / _frames.length) / estChunks;
                        onProgress({
                            phase: 'upscale',
                            done: totalFrames,
                            // Кадры, которые сеть правда считала (без повторов) —
                            // по ним страница учит «мс на тайл».
                            computed: totalFrames - dupFrames,
                            total: Math.max(totalFrames, estTotal),
                            pct: 25 + Math.min(1, frac) * 65,
                            etaSec: etaSec,
                            // Real measured per-frame wall time — includes
                            // inference + PNG decode/encode + canvas +
                            // MEMFS, i.e. everything the probe-based
                            // calibration can't see (measured on RTX 4060:
                            // probe covers ~81% of true cost; frame-level
                            // covers 100%). Consumers refine the ETA model
                            // from this after a few frames.
                            avgFrameMs: Math.round(avg),
                            facesMs: faceLane ? Math.round(faceLane.ms) : 0
                        });
                        try { window.dispatchEvent(new Event('stall-tick')); } catch (_) {}
                    }
                };
                var _runChunk = async function () {
                    if (window.__withStallGuard) {
                        // adaptive:true — the guard learns the real inter-tick
                        // cadence (per-TILE now, ~50-350 ms) via EMA and sets
                        // hard threshold to max(floorMs, 3×EMA). With per-tile
                        // ticks a healthy slow GPU never approaches the 3-min
                        // floor; a truly wedged device (ticks stop) still hard-
                        // aborts. softMs stays as the "still alive but slow"
                        // toast gate — now it only fires when a single TILE
                        // exceeds 2 min, which IS abnormal on any GPU.
                        await window.__withStallGuard(_doChunk, {
                            softMs: 120000, hardMs: 1800000, stage: 'ai_inference',
                            adaptive: true, floorMs: 180000,
                            meta: { slug: slug, chunk: ci }
                        });
                    } else {
                        await _doChunk();
                    }
                };
                var _chunkStartFrames = totalFrames, _chunkStartMs = frameMs.length, _chunkStartDups = dupFrames;
                var segName = 'ai_seg_' + String(ci).padStart(3, '0') + '.mp4';
                var segDone = false;
                if (encLane.lane === 'webcodecs') {
                    try {
                        await _runChunk();
                        _throwIfAborted(signal);
                        onProgress({ phase: 'chunk_encode', segmentIndex: ci, segmentCount: estChunks });
                        var segRes;
                        try { segRes = await segStream.finish(); } catch (finErr) { throw _encLaneError(finErr); }
                        segStream = null;
                        await ffApi.writeFile(segName, new Uint8Array(await segRes.blob.arrayBuffer()));
                        segDone = true;
                    } catch (laneErr) {
                        if (!laneErr || !laneErr.__encLane || (signal && signal.aborted)) throw laneErr;
                        /* Аппаратный кодер умер, и каскад примитива исчерпан —
                           фрагмент переделывается прежним лейном из входных PNG:
                           прогон не теряется, следующие фрагменты идут тем же
                           прежним лейном. */
                        try { if (segStream) segStream.abort(); } catch (_) {}
                        segStream = null;
                        _ae(slug, 'ai_encode_lane_fallback', { chunk: ci,
                            reason: String((laneErr.cause && laneErr.cause.message) || laneErr.message || '').slice(0, 100) });
                        encLane = { lane: 'ffmpeg', reason: 'stream-died' };
                        totalFrames = _chunkStartFrames;
                        frameMs.length = _chunkStartMs;
                        dupFrames = _chunkStartDups;
                    }
                    if (segDone) {
                        for (var ii = 0; ii < frames.length; ii++) { try { await ffApi.deleteFile(frames[ii]); } catch (_) {} }
                    }
                }
                if (!segDone) {
                    if (totalFrames === _chunkStartFrames) await _runChunk();
                    _throwIfAborted(signal);
                    onProgress({ phase: 'chunk_encode', segmentIndex: ci, segmentCount: estChunks });
                    // Encode this chunk's upscaled frames into an MP4 segment.
                    // -preset ultrafast: per-chunk libx264 in WASM was running at
                    // ~0.03× realtime (5 sec of video → 150 sec encode) on prod.
                    // ultrafast is 5-8× faster with ~2× larger intermediate files
                    // — fine because the final mux uses -c:v copy (no re-encode)
                    // so visual quality is the AI output unchanged. Trades
                    // intermediate disk for end-user wall-clock time.
                    // ⚠️ -video_track_timescale 120000 — the timescale openStream's muxer
                    // writes. The final `-f concat -c copy` needs ONE time base across
                    // segments: a run whose hardware encoder died mid-way mixes both
                    // lanes, and 1/120000 + 1/15360 came out with every later frame
                    // 1 tick apart (measured 16.10.2026, «Non-monotonic DTS»).
                    var segArgs = ['-framerate', String(extractFps), '-i', 'ai_out_%05d.png',
                        '-c:v', 'libx264', '-preset', 'ultrafast', '-crf', '18', '-pix_fmt', 'yuv420p',
                        '-r', String(extractFps), '-video_track_timescale', '120000', '-y', segName];
                    var segRc = await window.FFmpegProcessor.execGuarded(segArgs, {
                        softMs: 60000, hardMs: 600000, stage: 'ai_chunk_encode',
                        meta: { slug: slug, chunk: ci }
                    });
                    if (segRc !== 0) throw new Error('AI_CHUNK_ENCODE_FAILED chunk=' + ci);
                }
                _throwIfAborted(signal);
                segments.push(segName);

                // Emit a discrete "chunk done" event so the UI's segment
                // chip strip can advance one chip. Independent from the
                // throttled `onPartialReady` (which fires for cumulative
                // playable preview, not for every chunk).
                try {
                    onProgress({
                        phase: 'chunk_done',
                        segmentIndex: ci,
                        segmentCount: estChunks,
                        segmentSec: chunkSec
                    });
                } catch (_) {}

                // Streaming partial-preview (etalon from /ai-music-cleaner):
                // throttled cumulative `-c copy` concat so user can verify
                // AI output mid-job. Skips emission on the FINAL chunk —
                // the real result mux is about to overwrite the preview a
                // moment later, so the extra FFmpeg exec would be pure
                // overhead. Best-effort: a concat failure never aborts the
                // main job, partial preview is purely an UX bonus.
                if (onPartialReady && segments.length >= 1) {
                    var nowPartial = Date.now();
                    var contentSec = Math.min((ci + 1) * chunkSec, probe.duration || ((ci + 1) * chunkSec));
                    var wallSince = nowPartial - lastPartialWall;
                    var contentSince = contentSec - lastPartialContentSec;
                    // Always emit the FIRST one once segments.length >= 1.
                    // Subsequent: ≥ 3 wall-sec AND ≥ 5 content-sec since last.
                    var firstEmit = (partialEmitCount === 0);
                    if (firstEmit || (wallSince >= 3000 && contentSince >= 5)) {
                        // Burn the throttle window UPFRONT so a `_buildPartial`
                        // failure doesn't trigger another concat attempt on the
                        // very next chunk (was a 60-exec spam on a 60-chunk
                        // job before this guard). Success or failure, we treat
                        // the slot as consumed.
                        lastPartialWall = nowPartial;
                        lastPartialContentSec = contentSec;
                        partialEmitCount++;
                        try {
                            var partialBlob = await _buildPartial(ffApi, segments, slug);
                            onPartialReady({
                                blob: partialBlob,
                                secondsCommitted: contentSec,
                                totalSeconds: probe.duration || 0,
                                segmentIndex: ci,
                                segmentCount: estChunks,
                                isFirst: firstEmit
                            });
                            if (firstEmit) {
                                _ae(slug, 'ai_partial_first_emit', {
                                    after_sec: Math.round((nowPartial - aiStart) / 1000),
                                    chunk: ci
                                });
                            }
                        } catch (partialErr) {
                            // Best-effort path — log to console but never
                            // surface to the user. Telemetry once so we can
                            // see if a class of source consistently fails.
                            try { console.warn('[AiUpscalePipeline] partial concat failed:', partialErr); } catch (_) {}
                            if (firstEmit) {
                                _ae(slug, 'ai_partial_first_emit_failed', {
                                    chunk: ci,
                                    msg: ((partialErr && partialErr.message) || '').slice(0, 120)
                                });
                            }
                        }
                    }
                }

                for (var di = 1; di <= frames.length; di++) {
                    try { await ffApi.deleteFile('ai_out_' + String(di).padStart(5, '0') + '.png'); } catch (_) {}
                }
            }
        } finally {
            try { if (segStream) segStream.abort(); } catch (_) {}
            try { await mountHandle.dispose(); } catch (_) {}
        }
        if (!segments.length) throw new Error('AI_NO_FRAMES_EXTRACTED');
        _throwIfAborted(signal);

        // STEP 4 — concat segments + mux audio.
        onProgress({ phase: 'encode', done: 92, total: 100 });
        var outName = 'ai_out.mp4';
        var result;
        if (segments.length === 1 && !hasAudio) {
            var only = await ffApi.readFile(segments[0]);
            result = { blob: new Blob([only], { type: 'video/mp4' }), size: only.length };
        } else {
            var concatList = segments.map(function (s) { return "file '" + s + "'"; }).join('\n');
            await ffApi.writeFile('ai_concat.txt', new TextEncoder().encode(concatList));
            var muxArgs = ['-f', 'concat', '-safe', '0', '-i', 'ai_concat.txt'];
            if (hasAudio) muxArgs = muxArgs.concat(['-i', 'ai_audio.aac']);
            muxArgs = muxArgs.concat(['-c:v', 'copy']);
            // ai_audio.aac is AAC either way (copied or encoded once) — no second generation.
            if (hasAudio) muxArgs = muxArgs.concat(['-c:a', 'copy', '-shortest']);
            muxArgs.push('-movflags', '+faststart', '-y', outName);
            var muxRc = await window.FFmpegProcessor.execGuarded(muxArgs, {
                softMs: 60000, hardMs: 600000, stage: 'ai_mux',
                meta: { slug: slug, segments: segments.length },
                onProgress: function (p) {
                    onProgress({ phase: 'encode', done: 92 + Math.max(0, Math.min(1, p)) * 7, total: 100 });
                }
            });
            if (muxRc !== 0) throw new Error('AI_MUX_FAILED rc=' + muxRc);
            var muxed = await ffApi.readFile(outName);
            result = { blob: new Blob([muxed], { type: 'video/mp4' }), size: muxed.length };
        }

        // Cleanup MEMFS.
        try {
            for (var si = 0; si < segments.length; si++) {
                try { await ffApi.deleteFile(segments[si]); } catch (_) {}
            }
            try { await ffApi.deleteFile('ai_audio.aac'); } catch (_) {}
            try { await ffApi.deleteFile('ai_concat.txt'); } catch (_) {}
            try { await ffApi.deleteFile(outName); } catch (_) {}
        } catch (_) {}

        onProgress({ phase: 'done', done: 100, total: 100 });
        var stats = core.stats ? core.stats() : {};
        var avgFrameMs = frameMs.length ? Math.round(frameMs.reduce(function (a, b) { return a + b; }, 0) / frameMs.length) : 0;
        var totalSec = Math.round((Date.now() - aiStart) / 1000);

        _ae(slug, 'ai_upscale_complete', {
            scale: (scale && typeof scale === 'object') ? 'size' : scale,
            frames: totalFrames,
            avg_frame_ms: avgFrameMs,
            total_sec: totalSec,
            provider: stats.provider || '',
            tile_size: stats.tile_size || 0,
            out_size: result.size,
            out_w: dims.w, out_h: dims.h,
            lane: encLane ? encLane.lane : '',
            dup_frames: dupFrames,
            audio: hasAudio ? (audioCopied ? 'copy' : 'aac') : 'none',
            faces: faceLane ? 1 : 0,
            face_ms: faceLane && faceLane.frames ? Math.round(faceLane.ms / faceLane.frames) : 0,
            faces_found: faceLane ? faceLane.found : 0,
            faces_restored: faceLane ? faceLane.restored : 0
        });

        // Имя называет то, что внутри: «_ai_x4» стояло и у результата «До 4К».
        var _sfx = (typeof scale === 'object') ? (dims.w + 'x' + dims.h)
            : ({ hd720: '720p', fhd1080: '1080p', auto4k: '4k' }[scale] || 'x4');
        if (dims.ratio >= 4) _sfx = 'x4';
        var outName2 = (opts.file.name || 'video.mp4').replace(/\.[^.]+$/, '') + '_ai_' + _sfx + '.mp4';
        return {
            blob: result.blob,
            file: new File([result.blob], outName2, { type: 'video/mp4' }),
            stats: {
                frames: totalFrames,
                scale: dims.ratio,
                out_w: dims.w, out_h: dims.h,
                avg_frame_ms: avgFrameMs,
                total_sec: totalSec,
                provider: stats.provider || '',
                tile_size: stats.tile_size || 0,
                tiles: stats.tiles || 0,
                dup_frames: dupFrames,
                lane: encLane ? encLane.lane : '',
                faces_ms: faceLane ? Math.round(faceLane.ms) : 0,
                faces_restored: faceLane ? faceLane.restored : 0
            }
        };
    } catch (err) {
        // Treat a cancel as a cancel regardless of how it arrived.
        // Three shapes ALL count as "user-initiated":
        //   1. err.name === 'AbortError' (Promise-API standard)
        //   2. err.code === 'cancelled' (worker propagated)
        //   3. err.message contains 'AI_CANCELLED' (string-thrown from a
        //      _throwIfAborted callsite). This third case is what caused
        //      38 mislabels in 30 d — the worker's chunk-abort path
        //      throws Error('AI_CANCELLED') with name='Error', tripping
        //      the previous `err.name !== 'AbortError'` gate and
        //      inflating ai_upscale_failed.
        var msgStr = (err && err.message) || String(err) || '';
        var isCancel = !!(err && (
            err.name === 'AbortError' ||
            err.code === 'cancelled' ||
            msgStr.indexOf('AI_CANCELLED') !== -1
        ));
        if (!isCancel) {
            try { console.error('[AiUpscalePipeline] inference failed:', err); } catch (_) {}
            _ae(slug, 'ai_upscale_failed', {
                code: (err && err.code) || '',
                err_name: (err && err.name) || '',
                msg: msgStr.slice(0, 400),
                stack: ((err && err.stack) || '').slice(0, 600),
                scale: (scale && typeof scale === 'object') ? 'size' : scale,
                src_w: probe.width, src_h: probe.height,
                src_codec: (probe.codec || '').slice(0, 16),
                frames_done: totalFrames,
                segments_done: segments ? segments.length : 0,
                duration_s: Math.round(probe.duration || 0),
                probe_path: probe.probe_path || 'unknown',
                // Worker init timings + adapter info — close the diagnostic
                // gap "failure cohort has no hardware crosstab" from the
                // 2026-06-08 WebGPU audit. session_create_ms tells us if
                // we failed during graph compile vs probe; gpu_vendor +
                // architecture identify the Intel iGPU / NVIDIA / Apple
                // cohorts that drive the 87.5% failure share.
                session_create_ms: (err && err.sessionCreateMs) || 0,
                kernel_probe_ms: (err && err.kernelProbeMs) || 0,
                gpu_vendor: ((window.AIUpscaleCore && window.AIUpscaleCore.lastAdapterInfo && window.AIUpscaleCore.lastAdapterInfo.vendor) || '').slice(0, 16),
                gpu_architecture: ((window.AIUpscaleCore && window.AIUpscaleCore.lastAdapterInfo && window.AIUpscaleCore.lastAdapterInfo.architecture) || '').slice(0, 24),
                gpu_is_fallback: !!(window.AIUpscaleCore && window.AIUpscaleCore.lastAdapterInfo && window.AIUpscaleCore.lastAdapterInfo.isFallbackAdapter)
            });
        }
        throw err;
    } finally {
        try { unbindCancel(); } catch (_) {}
        if (!disposed && core) {
            disposed = true;
            try { core.dispose(); } catch (_) {}
        }
        _closeFaceLane(faceLane);
    }
}

/* ── Пробный участок (28.10.2026, отзыв #2033) ─────────────────────────────────
   Смотреть ли пробу до долгого прогона, решает человек; этот вызов — его кнопка.
   ОДИН кадр файла идёт тем же путём, что в run(): то же извлечение FFmpeg с тем
   же тонмапом HDR, тот же кадр в модель (полный, уменьшенный только там, где
   уменьшает run), та же модель, то же уменьшение до цели (_downscaleCanvas).
   В сеть уходит не кадр, а самый детальный квадрат: на телефоне кадр 1080×1920 —
   ~135 тайлов по ~3,4 с, квадрат 2×2 тайла — секунды. Рядом — тот же квадрат
   без модели, увеличенный до тех же размеров, и замеренное время модели: из него
   страница считает время всего ролика на ЭТОМ устройстве теми же тайлами, что
   оценка (planTiles). Предпросмотр — не выдача: кап не трогает.
   o: { file, probe?, scale, atSec?, perSide? (тайлов на сторону квадрата),
        signal?, slug?, onPhase?(name) }. */
function _detailBox(canvas, side) {
    var W = canvas.width, H = canvas.height;
    var sw = Math.min(side, W), sh = Math.min(side, H);
    if (sw >= W && sh >= H) return { x: 0, y: 0, w: W, h: H };
    var k = Math.max(1, Math.floor(Math.max(W, H) / 320));
    var gw = Math.max(1, Math.floor(W / k)), gh = Math.max(1, Math.floor(H / k));
    var g = document.createElement('canvas'); g.width = gw; g.height = gh;
    var gx = g.getContext('2d', { willReadFrequently: true });
    gx.drawImage(canvas, 0, 0, gw, gh);
    var d = gx.getImageData(0, 0, gw, gh).data;
    var lum = new Float32Array(gw * gh);
    for (var i = 0; i < gw * gh; i++) lum[i] = 0.2126 * d[4 * i] + 0.7152 * d[4 * i + 1] + 0.0722 * d[4 * i + 2];
    var en = new Float32Array(gw * gh);
    for (var y = 1; y < gh - 1; y++) {
        for (var x = 1; x < gw - 1; x++) {
            var c = y * gw + x;
            en[c] = Math.abs(lum[c + 1] - lum[c - 1]) + Math.abs(lum[c + gw] - lum[c - gw]);
        }
    }
    var bw = Math.max(1, Math.round(sw / k)), bh = Math.max(1, Math.round(sh / k));
    var step = Math.max(1, Math.floor(Math.min(bw, bh) / 4));
    var best = -1, bx = 0, by = 0;
    for (var oy = 0; oy + bh <= gh; oy += step) {
        for (var ox = 0; ox + bw <= gw; ox += step) {
            var sum = 0;
            for (var yy = oy; yy < oy + bh; yy += 2) for (var xx = ox; xx < ox + bw; xx += 2) sum += en[yy * gw + xx];
            if (sum > best) { best = sum; bx = ox; by = oy; }
        }
    }
    return { x: Math.max(0, Math.min(W - sw, bx * k)), y: Math.max(0, Math.min(H - sh, by * k)), w: sw, h: sh };
}

async function sample(o) {
    o = o || {};
    if (!o.file) throw new Error('AI_NO_FILE');
    var slug = o.slug || DEFAULT_SLUG;
    var scale = o.scale || 4;
    var signal = o.signal || null;
    var phase = typeof o.onPhase === 'function' ? o.onPhase : function () {};
    if (typeof window.AIUpscaleCore !== 'function') throw new Error('AI_CORE_NOT_LOADED');
    if (!window.FFmpegProcessor) throw new Error('AI_FFMPEG_NOT_LOADED');
    var probe = (o.probe && o.probe.width && o.probe.height)
        ? Object.assign({ duration: 0, isHdr: false }, o.probe) : await _probeSource(o.file);
    var dims = computeOutputDims(probe.width, probe.height, scale);
    var feed = computeFeedDims(probe.width, probe.height, scale);
    var tAll = Date.now();

    /* Модель грузится и компилируется ПАРАЛЛЕЛЬНО с извлечением кадра: обе работы
       ждут сеть и компиляцию, а не друг друга. Ядро освобождается на любом выходе. */
    var m = _resolveModel();
    var core = new window.AIUpscaleCore({ modelUrls: m.urls, expectedSha: m.sha, modelTile: m.tile, slug: slug });
    var unbind = _bindCancel(signal, function () { try { core.requestCancel(); } catch (_) {} });
    var tInit = Date.now(), initMs = 0;
    var ready = core.init({}).then(function () { initMs = Date.now() - tInit; });
    ready.catch(function () {});
    var res;
    try {
        phase('frame');
        var tFf = Date.now();
        var initOk = await window.FFmpegProcessor.initWithProgress(function () {}, { loading: 'AI', processing: 'AI' });
        if (!initOk) throw new Error('AI_FFMPEG_INIT_FAILED');
        var ffApi = window.FFmpegProcessor._getFFmpeg && window.FFmpegProcessor._getFFmpeg();
        if (!ffApi || typeof ffApi.exec !== 'function') throw new Error('AI_FFMPEG_RAW_UNAVAILABLE');
        var ffMs = Date.now() - tFf, tEx = Date.now();
        var tonemapVf = await _hdrTonemapArgs(ffApi, probe, slug);
        var at = (typeof o.atSec === 'number' && o.atSec >= 0) ? o.atSec : Math.max(0, (Number(probe.duration) || 0) * 0.5);
        var mh = await window.FFmpegProcessor.mountInput(o.file);
        var png = null;
        try {
            var rc = await window.FFmpegProcessor.execGuarded(
                ['-ss', at.toFixed(3), '-i', mh.path].concat(tonemapVf).concat(['-frames:v', '1', '-y', 'ai_sample.png']),
                { softMs: 30000, hardMs: 120000, stage: 'ai_sample_extract', meta: { slug: slug } });
            if (rc === 0) png = await ffApi.readFile('ai_sample.png');
            try { await ffApi.deleteFile('ai_sample.png'); } catch (_) {}
        } finally {
            try { await mh.dispose(); } catch (_) {}
        }
        var exMs = Date.now() - tEx;
        if (!png || png.length < 100) throw Object.assign(new Error('AI_SAMPLE_NO_FRAME'), { code: 'AI_SAMPLE_NO_FRAME' });
        _throwIfAborted(signal);
        var bmp = await createImageBitmap(new Blob([png], { type: 'image/png' }));
        var frame = document.createElement('canvas');
        frame.width = feed.w; frame.height = feed.h;
        var fx = frame.getContext('2d', { willReadFrequently: true });
        if (feed.w !== bmp.width || feed.h !== bmp.height) { fx.imageSmoothingEnabled = true; fx.imageSmoothingQuality = 'high'; }
        fx.drawImage(bmp, 0, 0, feed.w, feed.h);
        try { if (bmp.close) bmp.close(); } catch (_) {}

        var tile = m.tile || window.AIUpscaleCore.MODEL_TILE_DEFAULT || 64;
        var ov = window.AIUpscaleCore.TILE_OVERLAP || 16;
        var perSide = o.perSide || (window.AIUpscaleCore.isPhone() ? 2 : 3);
        var box = _detailBox(frame, perSide * (tile - ov) + ov);
        var crop = document.createElement('canvas');
        crop.width = box.w; crop.height = box.h;
        crop.getContext('2d').drawImage(frame, box.x, box.y, box.w, box.h, 0, 0, box.w, box.h);
        var outW = Math.max(1, Math.round(box.w * dims.w / feed.w)), outH = Math.max(1, Math.round(box.h * dims.h / feed.h));

        phase('model');
        var tWait = Date.now();
        await ready;
        var waitMs = Date.now() - tWait;
        var calib = core.tileMsMeasured || 0;
        _throwIfAborted(signal);
        phase('infer');
        var t1 = performance.now();
        var up = await core.upscaleFrame(crop, {});
        var inferMs = performance.now() - t1;
        var ai = await _downscaleCanvas(up, outW, outH);
        var plain = document.createElement('canvas');
        plain.width = outW; plain.height = outH;
        var pctx = plain.getContext('2d');
        pctx.imageSmoothingEnabled = true; pctx.imageSmoothingQuality = 'high';
        pctx.drawImage(crop, 0, 0, outW, outH);

        var tiles = (window.AIUpscaleShared && typeof window.AIUpscaleShared.planTiles === 'function')
            ? window.AIUpscaleShared.planTiles(box.w, box.h, tile, ov).length : perSide * perSide;
        var fx2src = probe.width / feed.w;
        res = {
            ai: ai, plain: plain, atSec: at, model: m.key || '',
            box: { x: Math.round(box.x * fx2src), y: Math.round(box.y * fx2src), w: Math.round(box.w * fx2src), h: Math.round(box.h * fx2src) },
            inferMs: Math.round(inferMs), initMs: initMs, ffMs: ffMs, exMs: exMs, waitMs: waitMs, tiles: tiles,
            tileMs: Math.max(1, Math.round(inferMs / Math.max(1, tiles))),
            calibTileMs: calib, totalMs: Date.now() - tAll
        };
    } finally {
        try { if (unbind) unbind(); } catch (_) {}
        try { core.dispose(); } catch (_) {}
    }
    _ae(slug, 'ai_sample', { model: res.model, tiles: res.tiles, infer_ms: res.inferMs, tile_ms: res.tileMs, calib_tile_ms: res.calibTileMs,
        init_ms: res.initMs, ff_ms: res.ffMs, ex_ms: res.exMs, wait_ms: res.waitMs, total_ms: res.totalMs, side: res.box.w,
        src_w: probe.width, src_h: probe.height, at: Math.round(res.atSec * 1000) / 1000, bx: res.box.x, by: res.box.y,
        mob: window.AIUpscaleCore.isPhone() ? 1 : 0 });
    return res;
}

window.AiUpscalePipeline = {
    run: run,
    sample: sample,
    isSupported: isSupported,
    computeOutputDims: computeOutputDims,
    computeFeedDims: computeFeedDims,
    targetRatio: targetRatio,
    // Exported for tests — chunk sizing is a memory-safety property
    // (Wave 24 lowMem clamp) and must stay pinnable.
    pickChunkSec: pickChunkSec
};

})();
